/**
 * SDK 설정 관리
 *
 * 초기화 옵션 검증 및 기본값 관리
 */

import type { LambAnalyticsOptions } from './sdk';

/**
 * 동의(consent) 기본값 단일 제어 플래그 — FRD 38 §10.
 *
 * ⚠️ 임시 정책: 동의 배너 구현 전까지 SDK 의 consent 기본값(init 에서 consent 미지정 시)을
 *    marketing/analytics 모두 **granted(true)** 로 둔다. 배너 없이도 광고/CAPI 가 동작하게 하기
 *    위함이다(host(lamb-site)도 동일 의미의 단일 플래그 CONSENT_DEFAULT_GRANTED 로 제어).
 *
 *    ▶ 프로덕션 전 반드시 `false` 로 되돌리고 동의 배너를 배선해야 한다
 *      (개인정보보호법: 분석/광고 동의 분리, 동의 전 식별자/전송 차단).
 *
 *    ※ 별도 패키지라 host 의 플래그를 import 할 수 없어 SDK 자체 단일 플래그를 둔다.
 *      host 가 init 에서 consent 객체를 명시 전달하면 그 값이 이 기본값을 덮어쓴다.
 */
export const CONSENT_DEFAULT_GRANTED = true;

/**
 * config 에서 해석/병합하는 옵션.
 * consent 는 init() 에서 직접 처리되며 ResolvedConfig 에 포함되지 않는다.
 */
type ResolvableOptions = Omit<LambAnalyticsOptions, 'consent'>;

/** 기본 설정값 */
export const DEFAULT_CONFIG: Required<ResolvableOptions> = {
  debug: false,
  autoTrack: {
    pageView: true,
    scroll: true,
    outboundClick: true,
    fileDownload: true,
    siteSearch: true,
    engagement: true,
  },
  ecommerce: {
    enabled: true,
    currency: 'KRW',
  },
  session: {
    timeout: 30,
  },
  transport: {
    method: 'beacon',
    batchSize: 10,
    flushInterval: 5000,
  },
};

/** 병합된 설정 타입 */
export type ResolvedConfig = Required<ResolvableOptions> & { siteId: string };

export class SdkConfig {
  private config: ResolvedConfig;

  constructor(siteId: string, options?: LambAnalyticsOptions) {
    this.config = this.merge(siteId, options);
  }

  private merge(siteId: string, options?: LambAnalyticsOptions): ResolvedConfig {
    return {
      siteId,
      debug: options?.debug ?? DEFAULT_CONFIG.debug,
      autoTrack: {
        ...DEFAULT_CONFIG.autoTrack,
        ...options?.autoTrack,
      },
      ecommerce: {
        ...DEFAULT_CONFIG.ecommerce,
        ...options?.ecommerce,
      },
      session: {
        ...DEFAULT_CONFIG.session,
        ...options?.session,
      },
      transport: {
        ...DEFAULT_CONFIG.transport,
        ...options?.transport,
      },
    };
  }

  get siteId(): string {
    return this.config.siteId;
  }

  get debug(): boolean {
    return this.config.debug;
  }

  get autoTrack(): Required<LambAnalyticsOptions>['autoTrack'] {
    return this.config.autoTrack;
  }

  get ecommerce(): Required<LambAnalyticsOptions>['ecommerce'] {
    return this.config.ecommerce;
  }

  get session(): Required<LambAnalyticsOptions>['session'] {
    return this.config.session;
  }

  get transport(): Required<LambAnalyticsOptions>['transport'] {
    return this.config.transport;
  }

  getAll(): ResolvedConfig {
    return { ...this.config };
  }
}
